/**
 * capital_wall.ts — the MEGAWALL and the plaza as PURE LAYOUT (capital wave
 * phase 3, 2026-09-15). No THREE: the dressing builder (capital_works.ts)
 * instances it, the proxy bake writes its silhouettes, the harness measures it.
 *
 * Coordinates are CAPITAL-LOCAL; `yaw` follows the dressing convention
 * (`atan2(dx, dz) + π/2` for a segment whose length runs along (dx, dz) — the
 * box's local x is its length). Heights are relative to the wall's base.
 */
import { CAPITAL_WALL_R, capitalGateAngles, civicCoreLayout, type Civilization } from '../../../shared/capitals.js';

export const WALL_T = 6;
export const BAY_M = 24;
export const WALL_H: Record<Civilization, number> = { a: 48, b: 64 };
export const TOWER_W = 14;
export const TOWER_OVER = 32;
export const OPENING = 26;
export const DECK_W = 4.5;
export const PARAPET_H = 1.6;
export const PARAPET_T = WALL_T - DECK_W;
export const CAP_H = 1.2;
export const BUTTRESS_W = 3;
export const BUTTRESS_OUT = 2;
export const LINTEL_H = 3;
/** Tower stairs: a square spiral inside each gate tower, climbable by the KCC's 0.8 m autostep. */
export const STEP_RISE = 0.75;
export const STEP_TREAD = 1.1;
export const STEP_W = 3;
export const STEPS_PER_FLIGHT = 10;
export const DOOR_W = 4;
export const DOOR_H = 4;
export const DECK_DOOR_H = 3;
/** Walk colliders along the wall are cut to this length (the hover slide reads solids per bay). */
export const COLLIDER_MAX = 16;

export interface WallBay { x: number; z: number; ang: number; yaw: number; len: number }
export interface WallTower { x: number; z: number; ang: number; yaw: number; gate: number; side: -1 | 1 }
export interface WallLintel { x: number; z: number; ang: number; yaw: number; len: number }
/** One stair block: the tread's centre, its top height, the flight's direction yaw. Blocks are solid from the base to `top`. */
export interface WallStep { x: number; z: number; top: number; yaw: number }
export interface WallLayout {
  r: number; h: number; towerH: number;
  gates: number[];
  bays: WallBay[]; towers: WallTower[]; lintels: WallLintel[];
  /** One flight list per tower (towers order). */
  stairs: WallStep[][];
  openings: Array<{ ang: number; half: number }>;
}

/** The dressing yaw of a tangent at polar angle `a` (length along the tangent). */
export function tangentYaw(a: number): number {
  return Math.atan2(-Math.sin(a), Math.cos(a)) + Math.PI / 2;
}

export function capitalWallLayout(theta0: number, civ: Civilization): WallLayout {
  const R = CAPITAL_WALL_R;
  const h = WALL_H[civ];
  const towerH = h + TOWER_OVER;
  const gates = capitalGateAngles(theta0).map((a) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)).sort((p, q) => p - q);
  const half = (OPENING / 2 + TOWER_W) / R; // the opening + a tower on each side, as an angle
  const bays: WallBay[] = [];
  for (let i = 0; i < gates.length; i++) {
    const g0 = gates[i], g1 = i + 1 < gates.length ? gates[i + 1] : gates[0] + Math.PI * 2;
    const start = g0 + half, end = g1 - half;
    const L = (end - start) * R;
    const n = Math.max(1, Math.round(L / BAY_M));
    const len = L / n;
    for (let j = 0; j < n; j++) {
      const a = start + ((j + 0.5) * len) / R;
      bays.push({ x: Math.cos(a) * R, z: Math.sin(a) * R, ang: a, yaw: tangentYaw(a), len });
    }
  }
  const towers: WallTower[] = [];
  const lintels: WallLintel[] = [];
  gates.forEach((g, gi) => {
    for (const side of [-1, 1] as const) {
      const a = g + (side * (OPENING / 2 + TOWER_W / 2)) / R;
      towers.push({ x: Math.cos(a) * R, z: Math.sin(a) * R, ang: a, yaw: tangentYaw(a), gate: gi, side });
    }
    lintels.push({ x: Math.cos(g) * R, z: Math.sin(g) * R, ang: g, yaw: tangentYaw(g), len: OPENING + 2 });
  });
  const stairs = towers.map((t) => towerStairs(t, h));
  return { r: R, h, towerH, gates, bays, towers, lintels, stairs, openings: gates.map((ang) => ({ ang, half: OPENING / 2 / R })) };
}

/**
 * A square spiral of solid blocks inside a tower: flights along the four inner
 * faces (3 m wide, 4.5 m from the centre), STEPS_PER_FLIGHT treads per face,
 * rising STEP_RISE per tread until the deck (h). Tower-local u = along the wall
 * tangent, v = radial; the door is on the inward (−v) face.
 */
export function towerStairs(t: WallTower, h: number): WallStep[] {
  const out: WallStep[] = [];
  const cu = Math.cos(t.ang + Math.PI / 2), su = Math.sin(t.ang + Math.PI / 2); // tangent
  const cv = Math.cos(t.ang), sv = Math.sin(t.ang);                             // radial (outward)
  const lane = TOWER_W / 2 - 1 - STEP_W / 2; // 4.5 m from the centre, inside the 1 m shell
  const span = STEPS_PER_FLIGHT * STEP_TREAD;  // 11 m along a face
  let i = 0;
  for (let f = 0; ; f++) {
    for (let k = 0; k < STEPS_PER_FLIGHT; k++) {
      const top = (i + 1) * STEP_RISE;
      if (top > h + STEP_RISE - 1e-9) return out;
      const tpos = (k + 0.5) * STEP_TREAD - span / 2;
      let u: number, v: number, dirU: number, dirV: number;
      switch (f % 4) {
        case 0: u = lane; v = tpos; dirU = 0; dirV = 1; break;
        case 1: u = -tpos; v = lane; dirU = -1; dirV = 0; break;
        case 2: u = -lane; v = -tpos; dirU = 0; dirV = -1; break;
        default: u = tpos; v = -lane; dirU = 1; dirV = 0; break;
      }
      const x = t.x + cu * u + cv * v, z = t.z + su * u + sv * v;
      const dx = cu * dirU + cv * dirV, dz = su * dirU + sv * dirV;
      out.push({ x, z, top: Math.min(top, h), yaw: Math.atan2(dx, dz) + Math.PI / 2 });
      i++;
      if (top >= h) return out;
    }
  }
}

/** Cut a length into ≤ COLLIDER_MAX pieces: [centre offset along the length, piece length]. */
export function colliderPieces(len: number): Array<[number, number]> {
  const n = Math.max(1, Math.ceil(len / COLLIDER_MAX));
  const piece = len / n;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < n; i++) out.push([(i + 0.5) * piece - len / 2, piece]);
  return out;
}

// ── The plaza (capital-local) ───────────────────────────────────────────────
export const FOUNTAIN_OFFSET_U = 40;
export const FOUNTAIN_R = [12, 8, 4.5] as const;
export const COLUMN_STEP = 8;
export const COLUMN_H = 9;
export const BENCH_RING_R = 18;
export const BENCH_COUNT = 12;
export const STAGE_SOCKETS = [-45, -15, 15, 45] as const;
export const STAGE_V = 70;

export interface PlazaLayout {
  cx: number; cz: number; yaw: number; hw: number; hd: number;
  fountain: { x: number; z: number };
  columns: Array<{ x: number; z: number }>;
  beams: Array<{ x: number; z: number; yaw: number; len: number }>;
  poles: Array<{ x: number; z: number; yaw: number }>;
  benches: Array<{ x: number; z: number; yaw: number }>;
  board: { x: number; z: number; yaw: number };
  stage: Array<{ x: number; z: number }>;
  /** NPC keep-clears (r) for the ceremonies' rooms. */
  clears: Array<{ x: number; z: number; r: number }>;
}

export function capitalPlazaLayout(theta0: number): PlazaLayout {
  const core = civicCoreLayout(theta0);
  const P = core.plaza;
  const ux = Math.cos(theta0), uz = Math.sin(theta0), vx = -Math.sin(theta0), vz = Math.cos(theta0);
  const at = (u: number, v: number): { x: number; z: number } => ({ x: ux * u + vx * v, z: uz * u + vz * v });
  const fountain = at(FOUNTAIN_OFFSET_U, 0);
  const columns: PlazaLayout['columns'] = [];
  const beams: PlazaLayout['beams'] = [];
  for (const sv of [-1, 1]) {
    const v = sv * (P.hd - 6);
    for (let u = -(P.hw - 10); u <= P.hw - 10 + 1e-6; u += COLUMN_STEP) columns.push(at(u, v));
    const c = at(0, v);
    beams.push({ x: c.x, z: c.z, yaw: Math.atan2(ux, uz) + Math.PI / 2, len: 2 * (P.hw - 10) + 2 });
  }
  const poles: PlazaLayout['poles'] = [];
  for (const su of [-1, 1]) for (let v = -80; v <= 80 + 1e-6; v += 20) { const p = at(su * (P.hw - 4), v); poles.push({ x: p.x, z: p.z, yaw: theta0 }); }
  const benches: PlazaLayout['benches'] = [];
  for (let i = 0; i < BENCH_COUNT; i++) {
    const a = (i / BENCH_COUNT) * Math.PI * 2;
    benches.push({ x: fountain.x + Math.cos(a) * BENCH_RING_R, z: fountain.z + Math.sin(a) * BENCH_RING_R, yaw: -a + Math.PI / 2 });
  }
  const b = at(-30, 0);
  const stage = STAGE_SOCKETS.map((u) => at(u, STAGE_V));
  const sc = at(0, STAGE_V);
  const clears = [
    { x: fountain.x, z: fountain.z, r: FOUNTAIN_R[0] + 2 },
    { x: sc.x, z: sc.z, r: 12 },
    ...core.parcels.map((pc) => ({ x: pc.cx, z: pc.cz, r: 70 })),
  ];
  return { cx: 0, cz: 0, yaw: theta0, hw: P.hw, hd: P.hd, fountain, columns, beams, poles, benches, board: { x: b.x, z: b.z, yaw: theta0 }, stage, clears };
}
